import * as PIXI from 'pixi.js';
import '@sgorg/pixi-ui';
import ElementComponent from '../../Component/UI/Element';
import GameObject from '../../GameObject/GameObject';
import Renderable from '../../Component/Renderable';
import UIImageComponent from '../../Component/UI/Image';
import Interactable from '../../Component/Interactable';
import UIScrollComponent from '../../Component/UI/Scroll';
import UIMaskComponent from '../../Component/UI/Mask';
import UIVerticalGroupComponent from '../../Component/UI/VerticalGroup';
import Point from '../../Utility/Point';
import UINineSliceComponent from '../../Component/Render/NineSlice';
import UITextComponent from '../../Component/UI/Text';
import UIRectComponent from '../../Component/UI/Rect';
import ColorFilter from '../../Component/ColorFilter';
import MaskComponent from '../../Component/Render/Mask';

export interface IElementContrains {
    heightMax: number;
    heightMin: number;
    widthMax: number;
    widthMin: number;
}

export interface IElementData {
    type?: string;
    name: string;
    layout: IElementLayout;
    children?: Array<any>;
    components?: Array<any>;
}

export interface IElementLayout {
    anchors?: [number, number, number, number];
    offsets?: [number, number, number, number];
    pivot?: [number, number];
    scale?: [number, number];
    angle?: number;
    constrains?: IElementContrains;
}

export default class Element extends GameObject<PIXI.ui.Element> {
    protected instantiateContainer(params: any): void {
        this.container = new PIXI.ui.Element();
    }

    protected onCreate(params: IElementData): void {
        this.addComponent<ElementComponent>(ElementComponent, {
            type: params.type,
            name: params.name,
            components: params.components,
            ...layoutToParams(params.layout),
        });

        // The renderable values alpha and visible will be changed by:
        // transparency and visibility
        const renderable: Renderable = this.addComponent<Renderable>(
            Renderable,
            params
        );

        // Parse our game components.
        if (params.components) {
            params.components.forEach((component: any) => {
                switch (component.type) {
                    case 'image':
                        this.addComponent(UIImageComponent, component);
                        break;
                    case 'input':
                        this.addComponent(Interactable, component.data);
                        break;
                    case 'nineslice':
                        this.addComponent(UINineSliceComponent, component);
                        break;
                    case 'rect':
                    case 'roundedrect':
                        this.addComponent(UIRectComponent, component);
                        break;
                    case 'text':
                        this.addComponent(UITextComponent, component);
                        break;
                    case 'transparency':
                        renderable.alpha = component.data.alpha;
                        break;
                    case 'visibility':
                        renderable.visible = !component.data.hidden;
                        break;
                    case 'scroll':
                        this.addComponent(UIScrollComponent, component);
                        break;
                    case 'mask':
                        this.addComponent(UIMaskComponent, component);
                        break;
                    case 'verticalgroup':
                        this.addComponent(UIVerticalGroupComponent, component);
                        break;
                    case 'colorfilter':
                        this.addComponent(ColorFilter, component.data);
                        break;
                    case 'texture_mask':
                        this.addComponent(MaskComponent, component.data);
                        break;
                }
            });
        }

        // Add element children.
        if (params.children) {
            params.children.forEach((child: any) => {
                this.addChild(
                    GameObject.Instantiate(Element, {
                        type: child.type,
                        name: child.name,
                        children: child.children,
                        components: child.components,
                        layout: child.layout,
                    })
                );
            });
        }
    }
}

export function layoutToParams(layout: IElementLayout): any {
    return {
        anchorTopLeft:
            layout && layout.anchors
                ? new Point(layout.anchors[0], layout.anchors[1])
                : new Point(0, 0),
        anchorBottomRight:
            layout && layout.anchors
                ? new Point(layout.anchors[2], layout.anchors[3])
                : new Point(1, 1),
        offsetTopLeft:
            layout && layout.offsets
                ? new Point(layout.offsets[0], layout.offsets[1])
                : new Point(0, 0),
        offsetBottomRight:
            layout && layout.offsets
                ? new Point(layout.offsets[2], layout.offsets[3])
                : new Point(0, 0),
        pivot:
            layout && layout.pivot
                ? new Point(layout.pivot[0], layout.pivot[1])
                : new Point(0.5, 0.5),
        scale:
            layout && layout.scale
                ? new Point(layout.scale[0], layout.scale[1])
                : new Point(1, 1),
        rotation: layout && layout.angle ? layout.angle * PIXI.DEG_TO_RAD : 0,
        heightMax:
            layout && layout.constrains ? layout.constrains.heightMax : 0,
        heightMin:
            layout && layout.constrains ? layout.constrains.heightMin : 0,
        widthMax: layout && layout.constrains ? layout.constrains.widthMax : 0,
        widthMin: layout && layout.constrains ? layout.constrains.widthMin : 0,
    };
}
